import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import { ArrowDown, ArrowUp, ArrowUpDown, BarChart3, Table2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/lib/formatters';
import { DiretoriaSqlButton } from '@/components/Diretoria/DiretoriaSqlButton';
import { DiretoriaPlacaFinanceiroModal } from '@/components/Diretoria/DiretoriaPlacaFinanceiroModal';
import { DiretoriaLocacoesDetalheModal } from '@/components/Diretoria/DiretoriaLocacoesDetalheModal';
import { DiretoriaBlockSkeleton } from '@/components/Diretoria/DiretoriaIndicatorCardSkeleton';

export interface LocacoesAnalyticsChartsData {
  mensal?: Array<{ name: string; novas: number; canceladas: number }>;
  mensalAnos?: number[];
  mensalComparativo?: Array<{ mes: number; name: string } & Record<string, number | string>>;
  diario: Array<{ name: string; novas: number; canceladas: number }>;
  topModeloMensal?: Array<{
    name: string;
    marca: string;
    modelo: string;
    anoModelo?: string;
    quantidade: number;
  }>;
  topModeloSeries?: Array<{
    key: string;
    name: string;
    marca?: string;
    modelo?: string;
    anoModelo?: string;
  }>;
  /** Um Jan–Dez por ano do contrato. Mesma chave de série em todos os painéis. */
  topModeloPorAno?: Array<{
    ano: number;
    pontos: Array<{ mes: number; name: string } & Record<string, number | string>>;
  }>;
  topModeloComparativo?: Array<{ mes: number; name: string } & Record<string, number | string>>;
  topPlacas: Array<{
    placa: string;
    marca: string;
    modelo: string;
    anoModelo?: string;
    empresa?: string;
    idEmpresa?: string;
    idEquipamento?: number;
    quantidade: number;
    totalFaturado?: number;
    totalCusto?: number;
    /** ABS(equipamentos.custo_inicial) — não entra na Diferença. */
    custoCompra?: number;
    diferenca?: number;
    diferencaPct?: number | null;
  }>;
  querySql?: string;
}

type TopPlacaRow = LocacoesAnalyticsChartsData['topPlacas'][number];
type PlacaSortKey =
  | 'placa'
  | 'modelo'
  | 'empresa'
  | 'quantidade'
  | 'totalFaturado'
  | 'totalCusto'
  | 'custoCompra'
  | 'diferenca';

function isoMonthRange(ano: number, mes: number | null): { from: string; to: string } {
  if (mes == null) return { from: `${ano}-01-01`, to: `${ano}-12-31` };
  const mm = String(mes).padStart(2, '0');
  const last = new Date(ano, mes, 0).getDate();
  return { from: `${ano}-${mm}-01`, to: `${ano}-${mm}-${String(last).padStart(2, '0')}` };
}

function formatModeloLabel(marca?: string, modelo?: string, anoModelo?: string): string {
  const base = [marca, modelo].filter(Boolean).join(' ').trim();
  if (!base) return '—';
  return anoModelo?.trim() ? `${base} · ${anoModelo.trim()}` : base;
}

function enrichTopPlacasWithCusto(
  rows: TopPlacaRow[],
  _motosCusto?: Array<{ placa: string; custoAtual: number }>
): TopPlacaRow[] {
  // Receita, custos e diferença já vêm do raio-x ERP. Este fallback não entra na fórmula.
  return rows;
}

function compareTopPlaca(a: TopPlacaRow, b: TopPlacaRow, key: PlacaSortKey): number {
  switch (key) {
    case 'placa':
      return a.placa.localeCompare(b.placa, 'pt-BR');
    case 'modelo':
      return formatModeloLabel(a.marca, a.modelo, a.anoModelo).localeCompare(
        formatModeloLabel(b.marca, b.modelo, b.anoModelo),
        'pt-BR'
      );
    case 'empresa':
      return String(a.empresa ?? '').localeCompare(String(b.empresa ?? ''), 'pt-BR');
    case 'quantidade':
      return (a.quantidade ?? 0) - (b.quantidade ?? 0);
    case 'totalFaturado':
      return (a.totalFaturado ?? 0) - (b.totalFaturado ?? 0);
    case 'totalCusto':
      return (a.totalCusto ?? 0) - (b.totalCusto ?? 0);
    case 'custoCompra':
      return (a.custoCompra ?? 0) - (b.custoCompra ?? 0);
    case 'diferenca':
      return (a.diferenca ?? 0) - (b.diferenca ?? 0);
    default:
      return 0;
  }
}

function SortableTh<K extends string>({
  label,
  sortKey,
  activeKey,
  dir,
  align = 'left',
  onSort,
}: {
  label: string;
  sortKey: K;
  activeKey: K;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right';
  onSort: (key: K) => void;
}) {
  const active = activeKey === sortKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`py-2 pr-2 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${
          align === 'right' ? 'flex-row-reverse' : ''
        } ${active ? 'text-foreground' : 'text-muted-foreground'}`}
      >
        <span>{label}</span>
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
      </button>
    </th>
  );
}

/** Criadas (sólido) e canceladas (tracejado) por ano — tons distintos desde 2025. */
const YEAR_PALETTE: Array<{ criadas: string; canceladas: string }> = [
  { criadas: 'hsl(222 47% 18%)', canceladas: '#ca8a04' },
  { criadas: 'hsl(199 89% 38%)', canceladas: '#ea580c' },
  { criadas: 'hsl(160 60% 32%)', canceladas: '#a16207' },
  { criadas: 'hsl(280 45% 40%)', canceladas: '#c2410c' },
];

/** Cores das barras por marca/modelo/ano. */
const SKU_PALETTE = [
  'hsl(222 47% 18%)',
  'hsl(199 89% 38%)',
  'hsl(160 60% 32%)',
  'hsl(280 45% 40%)',
  'hsl(25 80% 42%)',
  'hsl(340 55% 42%)',
  'hsl(45 70% 40%)',
  'hsl(200 35% 45%)',
  'hsl(120 40% 35%)',
  'hsl(300 35% 45%)',
  'hsl(15 60% 48%)',
  'hsl(250 40% 48%)',
];

const COLOR_NOVAS = 'hsl(222 47% 18%)';
const COLOR_CANCEL = '#ca8a04';

export const DiretoriaLocacoesCharts: React.FC<{
  data: LocacoesAnalyticsChartsData | undefined;
  /** Fallback de custos (indicador custo-por-moto) quando a analytics não trouxer custo. */
  motosCusto?: Array<{ placa: string; custoAtual: number }>;
  companyId?: string;
  /** True enquanto a query de charts ainda não retornou. */
  isLoading?: boolean;
}> = ({ data, motosCusto, companyId, isLoading = false }) => {
  const anos = data?.mensalAnos ?? [];
  const mensalComparativo = data?.mensalComparativo ?? [];
  const hasMensalComparativo = mensalComparativo.length === 12 && anos.length > 0;
  const sql = data?.querySql;

  const topModeloSeries = data?.topModeloSeries ?? [];
  const topModeloPorAno =
    data?.topModeloPorAno && data.topModeloPorAno.length > 0
      ? data.topModeloPorAno
      : (data?.topModeloComparativo?.length ?? 0) === 12
        ? [{ ano: 0, pontos: data!.topModeloComparativo! }]
        : [];
  const hasTopModeloComparativo = topModeloPorAno.length > 0 && topModeloSeries.length > 0;
  const topModeloYMax = hasTopModeloComparativo
    ? Math.max(
        1,
        ...topModeloPorAno.flatMap((painel) =>
          painel.pontos.flatMap((ponto) =>
            topModeloSeries.map((serie) => Number(ponto[serie.key]) || 0)
          )
        )
      )
    : 1;
  /** Só força largura extra (scroll) quando há muitas séries; senão ocupa 100% do card. */
  const topModeloNeedsScroll = topModeloSeries.length > 10;
  const topModeloChartMinWidthPx = topModeloNeedsScroll
    ? Math.max(960, 12 * topModeloSeries.length * 16)
    : undefined;

  const [sortKey, setSortKey] = useState<PlacaSortKey>('quantidade');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [topModeloView, setTopModeloView] = useState<'chart' | 'table'>('chart');
  const [modeloMesModal, setModeloMesModal] = useState<{
    marca: string;
    modelo: string;
    anoModelo: string;
    ano: number;
    mes: number | null;
    title: string;
    periodFrom: string;
    periodTo: string;
  } | null>(null);
  const [placaModal, setPlacaModal] = useState<{
    placa: string;
    idEquipamento?: number;
    modeloLabel?: string;
    empresa?: string;
  } | null>(null);

  const topPlacasSorted = useMemo(() => {
    const enriched = enrichTopPlacasWithCusto(data?.topPlacas ?? [], motosCusto);
    const sorted = [...enriched].sort((a, b) => compareTopPlaca(a, b, sortKey));
    if (sortDir === 'desc') sorted.reverse();
    return sorted;
  }, [data?.topPlacas, motosCusto, sortKey, sortDir]);

  const onSort = (key: PlacaSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'placa' || key === 'modelo' || key === 'empresa' ? 'asc' : 'desc');
  };

  const emptyOrLoading = (label: string, className = 'h-full') =>
    isLoading ? (
      <DiretoriaBlockSkeleton className={className} label={label} />
    ) : (
      <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Sem dados no período
      </p>
    );

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Locações mês a mês</CardTitle>
              <p className="text-xs text-muted-foreground">
                Jan–Dez · séries por ano desde 2025 · criadas = início no mês · canceladas =
                cancelamento no mês (empresa selecionada)
              </p>
            </div>
            <DiretoriaSqlButton sql={sql} source="cobranca" title="Locações mês a mês — consulta SQL" />
          </div>
        </CardHeader>
        <CardContent className="h-72 pt-0">
          {hasMensalComparativo ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mensalComparativo}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {anos.map((ano, i) => {
                  const palette = YEAR_PALETTE[i % YEAR_PALETTE.length]!;
                  return (
                    <React.Fragment key={ano}>
                      <Line
                        type="monotone"
                        dataKey={`novas_${ano}`}
                        name={`Criadas ${ano}`}
                        stroke={palette.criadas}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                      <Line
                        type="monotone"
                        dataKey={`canceladas_${ano}`}
                        name={`Canceladas ${ano}`}
                        stroke={palette.canceladas}
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={{ r: 3 }}
                      />
                    </React.Fragment>
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            emptyOrLoading('Carregando locações mês a mês')
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Locações dia a dia</CardTitle>
              <p className="text-xs text-muted-foreground">Criadas e cancelamentos no intervalo</p>
            </div>
            <DiretoriaSqlButton sql={sql} source="cobranca" title="Locações dia a dia — consulta SQL" />
          </div>
        </CardHeader>
        <CardContent className="h-64 pt-0">
          {(data?.diario?.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data!.diario}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="novas" name="Criadas" stroke={COLOR_NOVAS} strokeWidth={2} dot={false} />
                <Line
                  type="monotone"
                  dataKey="canceladas"
                  name="Canceladas"
                  stroke={COLOR_CANCEL}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            emptyOrLoading('Carregando locações dia a dia')
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80 xl:col-span-2">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Locações por marca / modelo / ano da moto</CardTitle>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                type="button"
                variant={topModeloView === 'chart' ? 'secondary' : 'ghost'}
                size="icon"
                title="Ver gráfico"
                aria-pressed={topModeloView === 'chart'}
                onClick={() => setTopModeloView('chart')}
              >
                <BarChart3 className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant={topModeloView === 'table' ? 'secondary' : 'ghost'}
                size="icon"
                title="Ver tabela de quantidades"
                aria-pressed={topModeloView === 'table'}
                onClick={() => setTopModeloView('table')}
              >
                <Table2 className="h-4 w-4" />
              </Button>
              <DiretoriaSqlButton
                sql={sql}
                source="cobranca"
                title="Locações por marca / modelo / ano da moto — consulta SQL"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 pt-0">
          {hasTopModeloComparativo ? (
            topModeloView === 'chart' ? (
              topModeloPorAno.map((painel, painelIndex) => (
                <div key={painel.ano} className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    {painel.ano > 0 ? painel.ano : 'Ano'}
                  </p>
                  <div className="h-72 w-full overflow-x-auto">
                    <div
                      className="h-full w-full min-w-full"
                      style={topModeloChartMinWidthPx ? { minWidth: topModeloChartMinWidthPx } : undefined}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={painel.pontos}
                          margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 11 }}
                            domain={[0, topModeloYMax]}
                          />
                          <Tooltip
                            formatter={(v: number, name: string) => [String(v), String(name)]}
                            labelFormatter={(label) =>
                              painel.ano > 0 ? `${String(label)}/${painel.ano}` : String(label)
                            }
                          />
                          {painelIndex === 0 ? <Legend wrapperStyle={{ fontSize: 10 }} /> : null}
                          {topModeloSeries.map((serie, i) => (
                            <Bar
                              key={serie.key}
                              dataKey={serie.key}
                              name={serie.name}
                              fill={SKU_PALETTE[i % SKU_PALETTE.length]}
                              radius={[4, 4, 0, 0]}
                              maxBarSize={36}
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              topModeloPorAno.map((painel) => {
                const meses = painel.pontos.map((ponto) => ({
                  key: String(ponto.mes),
                  name: String(ponto.name),
                }));
                const colTotals = meses.map((mes) =>
                  topModeloSeries.reduce((sum, serie) => {
                    const ponto = painel.pontos.find((p) => String(p.mes) === mes.key);
                    return sum + (Number(ponto?.[serie.key]) || 0);
                  }, 0)
                );
                const grand = colTotals.reduce((sum, n) => sum + n, 0);
                return (
                  <div key={painel.ano} className="space-y-1">
                    <p className="text-sm font-semibold text-foreground">
                      {painel.ano > 0 ? painel.ano : 'Ano'}
                    </p>
                    <div className="max-h-[28rem] overflow-auto rounded-md border border-border">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 z-[1]">
                          <tr className="border-b border-border bg-muted/80 text-left">
                            <th className="sticky left-0 bg-muted/95 px-3 py-2 font-medium text-foreground">
                              Marca / modelo / ano da moto
                            </th>
                            {meses.map((mes) => (
                              <th key={mes.key} className="px-2 py-2 text-right font-medium text-foreground">
                                {mes.name}
                              </th>
                            ))}
                            <th className="px-3 py-2 text-right font-medium text-foreground">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topModeloSeries.map((serie) => {
                            const values = meses.map((mes) => {
                              const ponto = painel.pontos.find((p) => String(p.mes) === mes.key);
                              return Number(ponto?.[serie.key]) || 0;
                            });
                            const rowTotal = values.reduce((sum, n) => sum + n, 0);
                            return (
                              <tr key={serie.key} className="border-b border-border/80 last:border-0">
                                <td className="sticky left-0 bg-background px-3 py-2 text-foreground">
                                  {serie.name}
                                </td>
                                {values.map((n, i) => {
                                  const mesNum = Number(meses[i]!.key);
                                  const canOpen =
                                    painel.ano >= 2000 &&
                                    mesNum >= 1 &&
                                    mesNum <= 12 &&
                                    Boolean(serie.marca && serie.modelo);
                                  return (
                                    <td key={meses[i]!.key} className="px-2 py-2 text-right tabular-nums">
                                      {canOpen ? (
                                        <button
                                          type="button"
                                          className="w-full cursor-pointer text-right tabular-nums text-primary hover:underline"
                                          title="Ver locações"
                                          onClick={() => {
                                            const range = isoMonthRange(painel.ano, mesNum);
                                            setModeloMesModal({
                                              marca: serie.marca!,
                                              modelo: serie.modelo!,
                                              anoModelo: serie.anoModelo ?? '',
                                              ano: painel.ano,
                                              mes: mesNum,
                                              title: `Locações · ${serie.name} · ${meses[i]!.name}/${painel.ano}`,
                                              periodFrom: range.from,
                                              periodTo: range.to,
                                            });
                                          }}
                                        >
                                          {n}
                                        </button>
                                      ) : (
                                        n
                                      )}
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2 text-right font-medium tabular-nums">
                                  {painel.ano >= 2000 && serie.marca && serie.modelo ? (
                                    <button
                                      type="button"
                                      className="w-full cursor-pointer text-right font-medium tabular-nums text-primary hover:underline"
                                      title="Ver locações"
                                      onClick={() => {
                                        const range = isoMonthRange(painel.ano, null);
                                        setModeloMesModal({
                                          marca: serie.marca!,
                                          modelo: serie.modelo!,
                                          anoModelo: serie.anoModelo ?? '',
                                          ano: painel.ano,
                                          mes: null,
                                          title: `Locações · ${serie.name} · ${painel.ano}`,
                                          periodFrom: range.from,
                                          periodTo: range.to,
                                        });
                                      }}
                                    >
                                      {rowTotal}
                                    </button>
                                  ) : (
                                    rowTotal
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-border bg-muted/40 font-medium">
                            <td className="sticky left-0 bg-muted/80 px-3 py-2">Total</td>
                            {colTotals.map((n, i) => (
                              <td key={meses[i]!.key} className="px-2 py-2 text-right tabular-nums">
                                {n}
                              </td>
                            ))}
                            <td className="px-3 py-2 text-right tabular-nums">{grand}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })
            )
          ) : (
            emptyOrLoading('Carregando locações por marca / modelo / ano da moto')
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80 xl:col-span-2">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Placas com mais locações</CardTitle>
              <p className="text-xs text-muted-foreground">
                Top 20 · histórico geral (exclui ERRO EM/DE CONTRATO) · Receita = baixas RECEBER do
                ERP · Diferença = receita − custos − custo compra · clique na placa para o raio-x
              </p>
            </div>
            <DiretoriaSqlButton
              sql={sql}
              source="cobranca"
              title="Placas com mais locações — consulta SQL"
            />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {topPlacasSorted.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="py-2 pr-2 text-left font-medium">#</th>
                    <SortableTh label="Placa" sortKey="placa" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                    <SortableTh label="Modelo" sortKey="modelo" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                    <SortableTh label="Empresa" sortKey="empresa" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                    <SortableTh
                      label="Locações"
                      sortKey="quantidade"
                      activeKey={sortKey}
                      dir={sortDir}
                      align="right"
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Receita"
                      sortKey="totalFaturado"
                      activeKey={sortKey}
                      dir={sortDir}
                      align="right"
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Custos"
                      sortKey="totalCusto"
                      activeKey={sortKey}
                      dir={sortDir}
                      align="right"
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Custo compra"
                      sortKey="custoCompra"
                      activeKey={sortKey}
                      dir={sortDir}
                      align="right"
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Diferença"
                      sortKey="diferenca"
                      activeKey={sortKey}
                      dir={sortDir}
                      align="right"
                      onSort={onSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {topPlacasSorted.map((row, i) => {
                    const diferenca = row.diferenca ?? 0;
                    const pct = row.diferencaPct;
                    return (
                      <tr key={`${row.placa}-${row.empresa ?? ''}-${i}`} className="border-b border-border/60">
                        <td className="py-2 pr-2 tabular-nums text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-2 font-medium">
                          <button
                            type="button"
                            className="text-left font-semibold text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() =>
                              setPlacaModal({
                                placa: row.placa,
                                idEquipamento: row.idEquipamento,
                                modeloLabel: formatModeloLabel(row.marca, row.modelo, row.anoModelo),
                                empresa: row.empresa,
                              })
                            }
                          >
                            {row.placa}
                          </button>
                        </td>
                        <td className="py-2 pr-2 text-muted-foreground">
                          {formatModeloLabel(row.marca, row.modelo, row.anoModelo)}
                        </td>
                        <td className="py-2 pr-2 text-muted-foreground">{row.empresa || '—'}</td>
                        <td className="py-2 pr-2 text-right tabular-nums font-semibold">{row.quantidade}</td>
                        <td className="py-2 pr-2 text-right tabular-nums">
                          {formatCurrency(row.totalFaturado ?? 0)}
                        </td>
                        <td className="py-2 pr-2 text-right tabular-nums">
                          {formatCurrency(row.totalCusto ?? 0)}
                        </td>
                        <td className="py-2 pr-2 text-right tabular-nums">
                          {formatCurrency(row.custoCompra ?? 0)}
                        </td>
                        <td className="py-2 text-right tabular-nums">
                          <span
                            className={
                              diferenca >= 0 ? 'text-emerald-700 font-medium' : 'text-red-700 font-medium'
                            }
                          >
                            {formatCurrency(diferenca)}
                          </span>
                          {pct != null ? (
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({pct >= 0 ? '+' : ''}
                              {pct.toFixed(1).replace('.', ',')}%)
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : isLoading ? (
            <DiretoriaBlockSkeleton className="h-40 w-full" label="Carregando placas com mais locações" />
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">Sem dados no período</p>
          )}
        </CardContent>
      </Card>

      <DiretoriaLocacoesDetalheModal
        open={Boolean(modeloMesModal)}
        onClose={() => setModeloMesModal(null)}
        kind={modeloMesModal ? 'modelo_mes' : null}
        companyId={companyId}
        periodFrom={modeloMesModal?.periodFrom ?? ''}
        periodTo={modeloMesModal?.periodTo ?? ''}
        title={modeloMesModal?.title}
        subtitle="Início do contrato nesta moto"
        modeloMes={
          modeloMesModal
            ? {
                marca: modeloMesModal.marca,
                modelo: modeloMesModal.modelo,
                anoModelo: modeloMesModal.anoModelo,
                ano: modeloMesModal.ano,
                mes: modeloMesModal.mes,
              }
            : null
        }
      />

      <DiretoriaPlacaFinanceiroModal
        open={Boolean(placaModal)}
        onClose={() => setPlacaModal(null)}
        placa={placaModal?.placa ?? ''}
        idEquipamento={placaModal?.idEquipamento}
        companyId={companyId}
        modeloLabel={placaModal?.modeloLabel}
        empresa={placaModal?.empresa}
      />
    </div>
  );
};
